/**
 * DevHud — corner overlay that surfaces engine perf + memory counters.
 *
 * Toggle: Ctrl+Shift+D (configurable via `hotkey` prop; pass `null` to
 * disable). Polls Command::RequestStats at the configured cadence and
 * mirrors the EngineStats payload + the latest PAINTED.paint_ms.
 *
 * QA value: every formatting / typing / open-doc operation is now
 * observable for heap growth + paint regression without devtools.
 */
import {
    createEffect,
    createSignal,
    For,
    onCleanup,
    Show,
    type Component,
} from 'solid-js';
import {
    createEditorCommands,
    createEditorState,
    type FontSubstitution,
    formatLayoutCaches,
    type LayoutCacheStats,
    openWarningCount,
    type ReadWarning,
    type RecoveryReport,
    type SaveNote,
    saveNoteCount,
    saveNoteKind,
} from '@nge/core';
import './DevHud.css';

export interface DevHudProps {
    /** Poll cadence in ms. Default 1000ms. Set high in prod, low in QA. */
    pollMs?: number;
    /** Keyboard chord (`Ctrl+Shift+D` by default). `null` disables hotkey. */
    hotkey?: string | null;
    /** Initial visibility. */
    defaultVisible?: boolean;
}

function fmtBytes(n: number | undefined): string {
    if (n === undefined) return '–';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MiB`;
}

/** Issue #500 - the layout-cache row's tooltip: the raw lookup counts
 *  behind the hit ratios. */
function layoutCacheTitle(s: LayoutCacheStats | undefined): string {
    if (s === undefined) return '';
    const n = (v: number) => v.toLocaleString('en-US');
    return (
        `paragraph cache: ${n(s.paragraph.hits)} hits, ${n(s.paragraph.misses)} misses; ` +
        `table cache: ${n(s.table.hits)} hits, ${n(s.table.misses)} misses ` +
        '(since the engine started)'
    );
}

/** Issue #315 — the base the last recovery came back from. */
function recoveryBase(r: RecoveryReport): string {
    const cause = r.cause === 'renderer-retry' ? 'renderer retry · ' : '';
    const base = r.restored ? (r.pinnedBase ? 'pinned snapshot' : 'snapshot') : 'command log';
    const at =
        r.baseSnapshotAt !== undefined
            ? ` @ ${new Date(r.baseSnapshotAt).toLocaleTimeString()}`
            : '';
    return `${cause}${base}${at} +${r.appliedCommands} cmds`;
}

/** Issue #406 — the open document's reader warnings, per kind. */
function openWarningKinds(ws: readonly ReadWarning[]): string {
    const byKind = new Map<string, number>();
    for (const w of ws) byKind.set(w.kind, (byKind.get(w.kind) ?? 0) + Math.max(1, w.count ?? 1));
    return [...byKind].map(([k, n]) => `${k} ×${n}`).join(', ');
}

/** Issue #485 — the last save's write notes, per kind. */
function saveNoteKinds(ns: readonly SaveNote[]): string {
    const byKind = new Map<string, number>();
    for (const n of ns) {
        const k = saveNoteKind(n);
        byKind.set(k, (byKind.get(k) ?? 0) + saveNoteCount([n]));
    }
    return [...byKind].map(([k, c]) => `${k} ×${c}`).join(', ');
}

/** Issue #315 — every way the last recovery degraded (`[]` = none). */
function recoveryLosses(r: RecoveryReport): string[] {
    const losses: string[] = [];
    if (r.logTruncated) losses.push('log truncated');
    if (r.tailDropped) losses.push('tail dropped');
    if (r.packageLost) losses.push('package lost');
    if (r.journalGap !== undefined && r.journalGap > 0) {
        losses.push(`journal gap (${r.journalGap} cmds)`);
    }
    if (r.refusedCommands !== undefined && r.refusedCommands > 0) {
        losses.push(`edits refused (${r.refusedCommands})`);
    }
    if (r.boundsRepaired !== undefined && r.boundsRepaired > 0) {
        losses.push(`values clamped (${r.boundsRepaired})`);
    }
    if (r.rendererDowngraded) losses.push('renderer downgraded');
    return losses;
}

/** Issue #329 — one substitution row: `Calibri → Carlito`, the
 *  complex-script slot marked `(CS)`. */
function substitutionLabel(s: FontSubstitution): string {
    const slot = s.slot === 'ComplexScript' ? ' (CS)' : '';
    return `${s.family}${slot} → ${s.substitute}`;
}

export const DevHud: Component<DevHudProps> = (props) => {
    const cmd = createEditorCommands();
    const state = createEditorState();
    const [visible, setVisible] = createSignal(props.defaultVisible ?? false);

    /* Hotkey wiring. */
    createEffect(() => {
        const chord = props.hotkey ?? 'Ctrl+Shift+D';
        if (chord === null) return;
        const handler = (e: KeyboardEvent) => {
            const ctrl = e.ctrlKey || e.metaKey;
            if (ctrl && e.shiftKey && e.key.toLowerCase() === 'd') {
                e.preventDefault();
                setVisible((v) => !v);
            }
        };
        window.addEventListener('keydown', handler);
        /* Sprint 8 (UI Edition) — SettingsMenu dispatches a custom
         * event so the HUD can be toggled from the gear popover too. */
        const onMenuToggle = () => setVisible((v) => !v);
        window.addEventListener('nge-toggle-hud', onMenuToggle);
        onCleanup(() => {
            window.removeEventListener('keydown', handler);
            window.removeEventListener('nge-toggle-hud', onMenuToggle);
        });
    });

    /* Stats polling. Only runs while visible. */
    createEffect(() => {
        if (!visible()) return;
        const ms = props.pollMs ?? 1000;
        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            try {
                await cmd.requestStats();
            } catch {
                /* worker may be trapped — next poll will retry */
            }
        };
        void tick();
        const id = window.setInterval(tick, ms);
        onCleanup(() => {
            cancelled = true;
            window.clearInterval(id);
        });
    });

    return (
        <Show when={visible()}>
            <div class="nge-hud" role="status" aria-live="off">
                <div class="nge-hud__title">
                    Engine HUD
                    <button
                        class="nge-hud__close"
                        type="button"
                        aria-label="Hide HUD"
                        onClick={() => setVisible(false)}
                    >
                        ×
                    </button>
                </div>
                <dl class="nge-hud__grid">
                    <dt>Renderer</dt>
                    <dd>{state.renderer()}</dd>

                    {/* Issue #99 — why this session is no longer on Vello.
                        Issue #240 — the downgrade is sticky across reloads
                        (persisted, 24 h decay); "Retry" forgets it and
                        probes the GPU again. Issue #270 — in place: the
                        engine restarts from the event log, the document
                        is kept (no reload). */}
                    <Show when={state.rendererDowngrade()}>
                        {(d) => (
                            <>
                                <dt>Fallback</dt>
                                <dd
                                    class="nge-hud__warn"
                                    title={`Forced ${d().to} after ${d().consecutive_traps} consecutive failures on ${d().from}; sticky across reloads for 24 h`}
                                >
                                    {d().from} → {d().to} ({d().consecutive_traps} traps)
                                </dd>
                                <Show when={cmd.canRetryGpuRenderer}>
                                    <dt />
                                    <dd>
                                        <button
                                            class="nge-hud__retry"
                                            type="button"
                                            title={`Forget the crash-loop record and restart the engine on ${d().from}; the document is kept`}
                                            onClick={() => void cmd.retryGpuRenderer()}
                                        >
                                            Retry {d().from}
                                        </button>
                                    </dd>
                                </Show>
                            </>
                        )}
                    </Show>

                    {/* Issue #315 — the most recent crash recovery: the base
                        it restored, the fallbacks it took, and every loss
                        (the same flags the recovery banner and the
                        telemetry CRASH sample carry). */}
                    <Show when={state.lastRecovery()}>
                        {(r) => (
                            <>
                                <dt>Recovery</dt>
                                <dd class="nge-hud__recovery-base">{recoveryBase(r())}</dd>
                                <dt>Fallbacks</dt>
                                <dd class="nge-hud__recovery-fallbacks">
                                    {r().snapshotFallbacks} snapshot · {r().packageFallbacks} package
                                </dd>
                                <dt>Losses</dt>
                                <dd
                                    class="nge-hud__recovery-losses"
                                    classList={{
                                        'nge-hud__recovery-losses--warn':
                                            recoveryLosses(r()).length > 0,
                                    }}
                                >
                                    {recoveryLosses(r()).join(', ') || 'none'}
                                </dd>
                            </>
                        )}
                    </Show>

                    {/* Issue #390 - the event log's health (the typed
                        `CheckpointState` event): checkpoints + the command
                        journal. */}
                    <dt>Event log</dt>
                    <dd
                        class="nge-hud__eventlog"
                        classList={{ 'nge-hud__warn': !state.checkpointState().ok }}
                        title={state.checkpointState().lastError ?? ''}
                    >
                        {state.checkpointState().ok
                            ? state.checkpointState().failures > 0
                                ? `retrying (${state.checkpointState().failures})`
                                : 'ok'
                            : state.checkpointState().journalFailing
                              ? 'journal failing'
                              : 'checkpoints failing'}
                    </dd>

                    {/* Issue #502 - a page canvas whose 2D context is lost and
                        not (yet) restored is blank until `contextrestored`
                        arrives (the typed `CanvasContext` event). */}
                    <dt>Canvas context</dt>
                    <dd
                        class="nge-hud__canvas-context"
                        classList={{ 'nge-hud__warn': state.canvasContext().lostPages.length > 0 }}
                        title={`${state.canvasContext().losses} lost, ${state.canvasContext().restores} restored`}
                    >
                        {state.canvasContext().lostPages.length > 0
                            ? `canvas context lost (page ${state.canvasContext().lostPages.map((p) => p + 1).join(', ')})`
                            : state.canvasContext().losses > 0
                              ? `ok (${state.canvasContext().restores}/${state.canvasContext().losses} restored)`
                              : 'ok'}
                    </dd>

                    {/* Issue #406 - the reader's warning report for the open
                        document (the same report the open-issues banner
                        and the telemetry DOC_OPEN sample carry). */}
                    <dt>Open issues</dt>
                    <dd
                        class="nge-hud__open-warnings"
                        classList={{ 'nge-hud__warn': state.openWarnings().length > 0 }}
                        title={openWarningKinds(state.openWarnings())}
                    >
                        {state.openWarnings().length > 0
                            ? `${openWarningCount(state.openWarnings())} (${openWarningKinds(state.openWarnings())})`
                            : 'none'}
                    </dd>

                    {/* Issue #485 - the writer's report for the last save
                        (the same notes the save-notes banner and the
                        telemetry DOC_SAVE sample carry). */}
                    <dt>Last save</dt>
                    <dd
                        class="nge-hud__save-notes"
                        classList={{ 'nge-hud__warn': state.lastSaveNotes().length > 0 }}
                        title={saveNoteKinds(state.lastSaveNotes())}
                    >
                        {state.lastSaveNotes().length > 0
                            ? `repaired (${saveNoteKinds(state.lastSaveNotes())})`
                            : 'clean'}
                    </dd>

                    {/* Issue #364 - the last engine error any command
                        answered (every error, toasted or not). */}
                    <Show when={state.lastError()}>
                        {(err) => (
                            <>
                                <dt>Last error</dt>
                                <dd
                                    class="nge-hud__lasterror nge-hud__warn"
                                    title={err().message}
                                >
                                    {err().command ?? '?'} · {err().kind ?? 'untyped'} · #
                                    {err().count}
                                </dd>
                            </>
                        )}
                    </Show>

                    <dt>WASM heap</dt>
                    <dd>{fmtBytes(state.stats()?.wasm_heap_bytes)}</dd>

                    <dt>Doc tree</dt>
                    <dd>{fmtBytes(state.stats()?.document_tree_bytes)}</dd>

                    <dt>Glyph cache</dt>
                    <dd>{state.stats()?.glyph_cache_entries ?? '–'}</dd>

                    {/* Issue #500 - the paragraph (#457) and table box (#379)
                        layout caches: estimated bytes / entries and the hit
                        ratio since the engine started, so a cache that
                        stops hitting (#497) shows here, not only as a slow
                        paint. Refreshed by the poll above. */}
                    <dt>Layout caches</dt>
                    <dd
                        class="nge-hud__layout-caches"
                        title={layoutCacheTitle(state.layoutCacheStats())}
                    >
                        {formatLayoutCaches(state.layoutCacheStats())}
                    </dd>

                    <dt>Undo depth</dt>
                    <dd>{state.stats()?.undo_stack_depth ?? '–'}</dd>

                    <dt>Fonts</dt>
                    <dd>{state.stats()?.fonts_resident ?? '–'}</dd>

                    {/* Issue #329 — the families the open document names
                        that the editor does not ship, and the faces
                        standing in for them (metric-compatible clones keep
                        the original's line breaks; the Arabic rows are a
                        closest-style pick). */}
                    <dt>Substituted</dt>
                    <dd class="nge-hud__substitutions">
                        <Show
                            when={state.fontSubstitutions().length > 0}
                            fallback={'none'}
                        >
                            <For each={state.fontSubstitutions()}>
                                {(sub) => (
                                    <span
                                        class="nge-hud__substitution"
                                        classList={{
                                            'nge-hud__substitution--approx':
                                                !sub.metric_compatible,
                                        }}
                                        data-family={sub.family}
                                        data-slot={sub.slot}
                                        title={
                                            sub.metric_compatible
                                                ? 'metric-compatible substitute'
                                                : 'closest-style substitute (widths differ)'
                                        }
                                    >
                                        {substitutionLabel(sub)}
                                    </span>
                                )}
                            </For>
                        </Show>
                    </dd>

                    <dt>Last paint</dt>
                    <dd>{state.lastPaintMs().toFixed(2)} ms</dd>

                    <dt>Can undo</dt>
                    <dd>{state.canUndo() ? 'yes' : 'no'}</dd>

                    <dt>Can redo</dt>
                    <dd>{state.canRedo() ? 'yes' : 'no'}</dd>
                </dl>
            </div>
        </Show>
    );
};
